import logger from "../utils/logger";
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import API_URL from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Users, Plus, Search, Upload, Download, ChevronRight, MapPin, Phone, Mail, Building2, User, Filter, CheckCircle2 } from 'lucide-react';
import ChangeHistory from '../components/ChangeHistory';

const STATUSES = [
    { value: 'new', label: 'New', color: 'bg-slate-100 text-slate-700' },
    { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-700' },
    { value: 'visited', label: 'Visited', color: 'bg-indigo-100 text-indigo-700' },
    { value: 'app_downloaded', label: 'App Downloaded', color: 'bg-purple-100 text-purple-700' },
    { value: 'lead_generated', label: 'Lead Generated', color: 'bg-amber-100 text-amber-700' },
    { value: 'loan_applied', label: 'Loan Applied', color: 'bg-cyan-100 text-cyan-700' },
    { value: 'disbursed', label: 'Disbursed', color: 'bg-green-100 text-green-700' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' },
];

const CRMLeads = () => {
    const { user, canManage, canEditRevenue } = useAuth();
    const [leads, setLeads] = useState([]);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterRM, setFilterRM] = useState('');
    const [addDialog, setAddDialog] = useState(false);
    const [bulkDialog, setBulkDialog] = useState(false);
    const [statusDialog, setStatusDialog] = useState(null);
    const [revenueDialog, setRevenueDialog] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const [form, setForm] = useState({ name: '', lead_type: 'institution', category_id: '', contact_person: '', phone: '', email: '', address: '', city: '', state: '', notes: '' });
    const [assignRM, setAssignRM] = useState('');
    const [statusForm, setStatusForm] = useState({ status: '', notes: '', loan_amount: '', product_id: '' });
    const [revenueForm, setRevenueForm] = useState({ loan_amount: '', gross_revenue: '', customer_offer: '', product_id: '' });
    const [stages, setStages] = useState([]);

    const fetchData = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filterCategory) params.append('category_id', filterCategory);
            if (filterStatus) params.append('status', filterStatus);
            if (filterRM) params.append('assigned_rm', filterRM);
            if (filterType) params.append('lead_type', filterType);
            const [leadsRes, catsRes, prodsRes, empRes, stageRes] = await Promise.all([
                axios.get(`${API_URL}/api/crm/leads?${params}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/crm/categories`, { withCredentials: true }),
                axios.get(`${API_URL}/api/crm/products`, { withCredentials: true }),
                canManage ? axios.get(`${API_URL}/api/employees`, { withCredentials: true }) : Promise.resolve({ data: [] }),
                axios.get(`${API_URL}/api/crm/lead-stages`, { withCredentials: true }),
            ]);
            setLeads(leadsRes.data);
            setCategories(catsRes.data);
            setProducts(prodsRes.data);
            setEmployees(empRes.data.filter(e => e.role !== 'admin'));
            setStages(stageRes.data.stages || []);
        } catch (error) { logger.error('CRM fetch:', error); }
        finally { setLoading(false); }
    }, [filterCategory, filterStatus, filterRM, filterType, canManage]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/crm/leads${assignRM ? `?assigned_rm=${assignRM}` : ''}`, form, { withCredentials: true });
            setAddDialog(false);
            setForm({ name: '', lead_type: 'institution', category_id: '', contact_person: '', phone: '', email: '', address: '', city: '', state: '', notes: '' });
            setAssignRM('');
            fetchData();
        } catch (error) { alert(error.response?.data?.detail || 'Error'); }
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const url = `${API_URL}/api/crm/leads/bulk-upload${assignRM ? `?assigned_rm=${assignRM}` : ''}`;
            const { data } = await axios.post(url, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            alert(`${data.created} leads uploaded successfully${data.errors?.length ? `\n${data.errors.length} errors` : ''}`);
            setBulkDialog(false);
            fetchData();
        } catch (error) { alert(error.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
    };

    const downloadTemplate = async () => {
        const response = await axios.get(`${API_URL}/api/crm/leads/download-template`, { withCredentials: true, responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'leads_template.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleStatusUpdate = async () => {
        try {
            await axios.put(`${API_URL}/api/crm/leads/${statusDialog}/status`, {
                ...statusForm, loan_amount: statusForm.loan_amount ? parseFloat(statusForm.loan_amount) : null
            }, { withCredentials: true });
            setStatusDialog(null);
            setStatusForm({ status: '', notes: '', loan_amount: '', product_id: '' });
            fetchData();
        } catch (error) { alert(error.response?.data?.detail || 'Error'); }
    };

    const handleRevenueUpdate = async () => {
        try {
            const selectedProduct = products.find(p => p._id === revenueForm.product_id);
            const commissionRate = selectedProduct?.commission_rate || 0;
            const loanAmt = parseFloat(revenueForm.loan_amount) || 0;
            const customerOffer = parseFloat(revenueForm.customer_offer) || 0;

            const payload = {
                lead_id: revenueDialog,
                product_id: revenueForm.product_id || null,
                loan_amount: loanAmt,
                customer_offer: customerOffer,
            };
            // Smart fallback: send manual gross_revenue only when no product/zero commission
            if (!(commissionRate > 0)) {
                payload.gross_revenue = parseFloat(revenueForm.gross_revenue) || 0;
            }

            await axios.put(`${API_URL}/api/crm/leads/${revenueDialog}/revenue`, payload, { withCredentials: true });
            setRevenueDialog(null);
            setRevenueForm({ loan_amount: '', gross_revenue: '', customer_offer: '', product_id: '' });
            fetchData();
        } catch (error) { alert(error.response?.data?.detail || 'Error'); }
    };

    const filtered = leads.filter(l => !search || l.name.toLowerCase().includes(search.toLowerCase()) || l.contact_person?.toLowerCase().includes(search.toLowerCase()));
    const getStatusBadge = (s) => STATUSES.find(st => st.value === s) || STATUSES[0];

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading...</div></div>;

    return (
        <div className="space-y-6" data-testid="crm-leads-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>CRM - Leads</h1>
                    <p className="text-sm text-slate-500 mt-1">{canManage ? 'Manage all leads & assignments' : 'My assigned leads'}</p>
                </div>
                <div className="flex gap-2">
                    {canManage && (
                        <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
                            <DialogTrigger asChild><Button variant="outline" className="rounded-lg" data-testid="bulk-upload-btn"><Upload className="h-4 w-4 mr-2" />Bulk Upload</Button></DialogTrigger>
                            <DialogContent className="rounded-none max-w-md">
                                <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Bulk Upload Leads</DialogTitle><DialogDescription>Upload an Excel sheet of leads. Flexible column names (Name, Lead Name, Institution Name).</DialogDescription></DialogHeader>
                                <div className="space-y-4 mt-4">
                                    <Button variant="outline" className="rounded-none w-full" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="h-4 w-4 mr-2" />Download Excel Template</Button>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Assign RM</Label>
                                        <select value={assignRM} onChange={(e) => setAssignRM(e.target.value)} className="w-full rounded-none border border-slate-200 px-3 py-2 text-sm mt-1">
                                            <option value="">Auto (Self)</option>
                                            {employees.map(e => <option key={e._id} value={e._id}>{e.name}</option>)}
                                        </select></div>
                                    <Button className="bg-brand hover:bg-brand rounded-none w-full" onClick={() => fileRef.current?.click()} disabled={uploading}>
                                        <Upload className="h-4 w-4 mr-2" />{uploading ? 'Uploading...' : 'Choose Excel File'}
                                    </Button>
                                    <input ref={fileRef} type="file" className="hidden" onChange={handleBulkUpload} accept=".xlsx,.xls" />
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                    <Dialog open={addDialog} onOpenChange={setAddDialog}>
                        <DialogTrigger asChild><Button className="bg-brand hover:bg-brand rounded-lg shadow-sm" data-testid="add-lead-btn"><Plus className="h-4 w-4 mr-2" />Add Lead</Button></DialogTrigger>
                        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
                            <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Add New Lead</DialogTitle><DialogDescription>Create a new institution or individual lead. You can update status and revenue after creation.</DialogDescription></DialogHeader>
                            <form onSubmit={handleCreate} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Lead Name</Label>
                                        <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-none mt-1" required data-testid="lead-name-input" /></div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Type</Label>
                                        <Select value={form.lead_type} onValueChange={(v) => setForm({...form, lead_type: v})}>
                                            <SelectTrigger className="rounded-none mt-1" data-testid="lead-type-select"><SelectValue /></SelectTrigger>
                                            <SelectContent className="rounded-none"><SelectItem value="institution">Institution</SelectItem><SelectItem value="individual">Individual</SelectItem></SelectContent>
                                        </Select></div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Category</Label>
                                        <select value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})} className="w-full rounded-none border border-slate-200 px-3 py-2 text-sm mt-1">
                                            <option value="">Select Category</option>
                                            {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                        </select></div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Contact Person</Label>
                                        <Input value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} className="rounded-none mt-1" /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Phone</Label>
                                        <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="rounded-none mt-1" /></div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Email</Label>
                                        <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-none mt-1" /></div>
                                </div>
                                <div><Label className="text-xs font-bold uppercase text-slate-500">Address</Label>
                                    <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="rounded-none mt-1" /></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">City</Label>
                                        <Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="rounded-none mt-1" /></div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">State</Label>
                                        <Input value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} className="rounded-none mt-1" /></div>
                                </div>
                                {canManage && <div><Label className="text-xs font-bold uppercase text-slate-500">Assign RM</Label>
                                    <select value={assignRM} onChange={(e) => setAssignRM(e.target.value)} className="w-full rounded-none border border-slate-200 px-3 py-2 text-sm mt-1">
                                        <option value="">Self</option>
                                        {employees.map(e => <option key={e._id} value={e._id}>{e.name}</option>)}
                                    </select></div>}
                                <div><Label className="text-xs font-bold uppercase text-slate-500">Notes</Label>
                                    <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="rounded-none mt-1" rows={2} /></div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" className="rounded-none" onClick={() => setAddDialog(false)}>Cancel</Button>
                                    <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-lead-btn">Create Lead</Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                <div className="relative flex-1 max-w-xs"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search leads..." className="pl-9 rounded-lg h-9 bg-slate-50 border-slate-200" data-testid="search-leads" /></div>
                <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[130px] rounded-lg h-9 bg-slate-50 border-slate-200"><SelectValue placeholder="All Types" /></SelectTrigger>
                    <SelectContent className="rounded-lg"><SelectItem value="all">All Types</SelectItem><SelectItem value="institution">Institution</SelectItem><SelectItem value="individual">Individual</SelectItem></SelectContent>
                </Select>
                <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[130px] rounded-lg h-9 bg-slate-50 border-slate-200"><SelectValue placeholder="All Categories" /></SelectTrigger>
                    <SelectContent className="rounded-lg"><SelectItem value="all">All Categories</SelectItem>{categories.map(c => <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[140px] rounded-lg h-9 bg-slate-50 border-slate-200"><SelectValue placeholder="All Status" /></SelectTrigger>
                    <SelectContent className="rounded-lg"><SelectItem value="all">All Status</SelectItem>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
                {canManage && <Select value={filterRM || "all"} onValueChange={(v) => setFilterRM(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[150px] rounded-lg h-9 bg-slate-50 border-slate-200"><SelectValue placeholder="All RMs" /></SelectTrigger>
                    <SelectContent className="rounded-lg"><SelectItem value="all">All RMs</SelectItem>{employees.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>}
                <span className="ml-auto text-[11px] text-slate-500 font-medium">Showing <span className="font-bold text-slate-900">{filtered.length}</span> {filtered.length === 1 ? 'lead' : 'leads'}</span>
            </div>

            {/* Leads Table */}
            <Card className="rounded-xl border-slate-200 shadow-sm overflow-hidden"><CardContent className="p-0 overflow-x-auto">
                <table className="w-full"><thead><tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Lead</th>
                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Type</th>
                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Category</th>
                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Contact</th>
                    {canManage && <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">RM</th>}
                    <th className="text-center p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="text-right p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Revenue</th>
                    <th className="text-right p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr></thead><tbody>
                    {filtered.map(lead => {
                        const st = getStatusBadge(lead.status);
                        return (
                            <tr key={lead._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lead-${lead._id}`}>
                                <td className="p-3"><p className="font-medium text-slate-900">{lead.name}</p><p className="text-[10px] text-slate-400">{lead.city}{lead.state ? `, ${lead.state}` : ''}</p></td>
                                <td className="p-3"><Badge className={`rounded-none text-[10px] ${lead.lead_type === 'institution' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'}`}>{lead.lead_type}</Badge></td>
                                <td className="p-3 text-sm text-slate-600">{lead.category_name || '-'}</td>
                                <td className="p-3"><p className="text-sm text-slate-700">{lead.contact_person || '-'}</p>{lead.phone && <p className="text-[10px] text-slate-400">{lead.phone}</p>}</td>
                                {canManage && <td className="p-3 text-sm text-slate-600">{lead.assigned_rm_name}</td>}
                                <td className="p-3 text-center"><Badge className={`rounded-none text-[10px] ${st.color}`}>{st.label}</Badge></td>
                                <td className="p-3 text-right">{lead.net_revenue > 0 ? <span className="font-bold text-green-700">Rs.{lead.net_revenue.toLocaleString()}</span> : <span className="text-slate-400">-</span>}</td>
                                <td className="p-3 text-right">
                                    <div className="flex justify-end gap-1">
                                        <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => { setStatusDialog(lead._id); setStatusForm({ status: lead.status, notes: '', loan_amount: lead.loan_amount || '', product_id: lead.product_id || '' }); }} data-testid={`update-status-${lead._id}`}>Status</Button>
                                        {canEditRevenue && <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => { setRevenueDialog(lead._id); setRevenueForm({ loan_amount: lead.loan_amount || '', gross_revenue: lead.gross_revenue || '', customer_offer: lead.customer_offer || '', product_id: lead.product_id || '' }); }} data-testid={`update-revenue-${lead._id}`}>Revenue</Button>}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    {filtered.length === 0 && <tr><td colSpan={canManage ? 8 : 7} className="p-8 text-center text-slate-500">No leads found</td></tr>}
                </tbody></table>
            </CardContent></Card>

            {/* Status Update Dialog */}
            <Dialog open={!!statusDialog} onOpenChange={(o) => !o && setStatusDialog(null)}>
                <DialogContent className="rounded-2xl max-w-md">
                    <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Update Lead Status</DialogTitle><DialogDescription>Move the lead through the funnel. Your change is recorded in the status history.</DialogDescription></DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div><Label className="text-xs font-bold uppercase text-slate-500">Status</Label>
                            <Select value={statusForm.status} onValueChange={(v) => setStatusForm({...statusForm, status: v})}>
                                <SelectTrigger className="rounded-lg mt-1 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-lg">
                                    {stages.length > 0 ? (
                                        <>
                                            <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50">Acquisition (RM)</div>
                                            {stages.filter(s => s.group === 'acquisition').map(s => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                            <div className="px-2 py-1 mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50">File Workflow (Ops)</div>
                                            {stages.filter(s => s.group === 'ops').map(s => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </>
                                    ) : (
                                        STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)
                                    )}
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-slate-500 mt-1.5">{stages.find(s => s.value === statusForm.status)?.group === 'ops' ? '🛠 Ops Team will see this update — RM and customer get auto-notified via email + SMS.' : '👋 RM-driven stage in the acquisition funnel.'}</p>
                        </div>
                        {(statusForm.status === 'loan_applied' || statusForm.status === 'disbursed' || statusForm.status === 'sanctioned') && (
                            <><div><Label className="text-xs font-bold uppercase text-slate-500">Loan Amount</Label>
                                <Input type="number" value={statusForm.loan_amount} onChange={(e) => setStatusForm({...statusForm, loan_amount: e.target.value})} className="rounded-lg mt-1 bg-slate-50 border-slate-200" /></div>
                            <div><Label className="text-xs font-bold uppercase text-slate-500">Product</Label>
                                <select value={statusForm.product_id} onChange={(e) => setStatusForm({...statusForm, product_id: e.target.value})} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1">
                                    <option value="">Select Product</option>
                                    {products.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                </select></div></>
                        )}
                        <div><Label className="text-xs font-bold uppercase text-slate-500">Notes</Label>
                            <Textarea value={statusForm.notes} onChange={(e) => setStatusForm({...statusForm, notes: e.target.value})} className="rounded-lg mt-1 bg-slate-50 border-slate-200" rows={2} /></div>

                        {/* Change History sidebar */}
                        <div className="border-t border-slate-200 pt-3">
                            <ChangeHistory resourceType="lead" resourceId={statusDialog} limit={15} compact />
                        </div>

                        <div className="flex justify-end gap-3"><Button variant="outline" className="rounded-lg" onClick={() => setStatusDialog(null)}>Cancel</Button>
                            <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" onClick={handleStatusUpdate}>Update</Button></div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Revenue Dialog */}
            <Dialog open={!!revenueDialog} onOpenChange={(o) => !o && setRevenueDialog(null)}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="revenue-dialog">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Update Revenue</DialogTitle>
                        <DialogDescription>Pick a product (with commission %) and enter loan amount — gross & net revenue auto-calculate. Or override manually if no product is configured.</DialogDescription>
                    </DialogHeader>
                    {(() => {
                        const selectedProduct = products.find(p => p._id === revenueForm.product_id);
                        const commissionRate = selectedProduct?.commission_rate || 0;
                        const loanAmt = parseFloat(revenueForm.loan_amount) || 0;
                        const customerOffer = parseFloat(revenueForm.customer_offer) || 0;
                        const autoCalc = commissionRate > 0;
                        const grossPreview = autoCalc
                            ? Math.round(loanAmt * commissionRate / 100 * 100) / 100
                            : (parseFloat(revenueForm.gross_revenue) || 0);
                        const netPreview = grossPreview - customerOffer;
                        return (
                            <div className="space-y-4 mt-4">
                                <div>
                                    <Label className="text-xs font-bold uppercase text-slate-500">Product</Label>
                                    <select
                                        value={revenueForm.product_id}
                                        onChange={(e) => setRevenueForm({ ...revenueForm, product_id: e.target.value })}
                                        className="w-full rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#2563eb] px-3 py-2 text-sm mt-1"
                                        data-testid="revenue-product-select"
                                    >
                                        <option value="">— Select Product —</option>
                                        {products.map(p => (
                                            <option key={p._id} value={p._id}>
                                                {p.name}{(p.commission_rate || 0) > 0 ? ` · ${p.commission_rate}%` : ' · no rate'}
                                            </option>
                                        ))}
                                    </select>
                                    {autoCalc ? (
                                        <p className="text-[11px] text-emerald-600 mt-1.5 flex items-center gap-1">
                                            <CheckCircle2 className="h-3 w-3" /> Auto-calc on: <strong>{commissionRate}%</strong> of loan amount
                                        </p>
                                    ) : (
                                        <p className="text-[11px] text-amber-700 mt-1.5">
                                            No commission rate on this product — enter Gross Revenue manually below.
                                        </p>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs font-bold uppercase text-slate-500">Loan Amount</Label>
                                        <Input
                                            type="number"
                                            value={revenueForm.loan_amount}
                                            onChange={(e) => setRevenueForm({ ...revenueForm, loan_amount: e.target.value })}
                                            className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                            data-testid="revenue-loan-amount"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase text-slate-500">Gross Revenue</Label>
                                        {autoCalc ? (
                                            <div className="mt-1 p-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 font-bold text-sm tabular-nums" data-testid="revenue-gross-auto">
                                                Rs.{grossPreview.toLocaleString()}
                                            </div>
                                        ) : (
                                            <Input
                                                type="number"
                                                value={revenueForm.gross_revenue}
                                                onChange={(e) => setRevenueForm({ ...revenueForm, gross_revenue: e.target.value })}
                                                className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                                data-testid="revenue-gross-manual"
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs font-bold uppercase text-slate-500">Customer Offer</Label>
                                        <Input
                                            type="number"
                                            value={revenueForm.customer_offer}
                                            onChange={(e) => setRevenueForm({ ...revenueForm, customer_offer: e.target.value })}
                                            className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                            data-testid="revenue-customer-offer"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase text-slate-500">Net Revenue</Label>
                                        <div className="mt-1 p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 font-bold text-sm tabular-nums" data-testid="revenue-net-preview">
                                            Rs.{netPreview.toLocaleString()}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-2">
                                    <Button variant="outline" className="rounded-lg" onClick={() => setRevenueDialog(null)}>Cancel</Button>
                                    <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" onClick={handleRevenueUpdate} data-testid="revenue-save-btn">Save Revenue</Button>
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CRMLeads;
